/**
 *
 * @module BasePlayer
 * @desc Base implementation of a concrete player
 * @class BasePlayer
 *
 */
import _get from 'lodash/get';
import Fullscreen from 'fscreen';
import ResizeObserver from 'resize-observer-polyfill';
import AbstractPlayer from './abstract-player';
import Log from '../util/logger';
import MediaPlayerEvents from '../events/events';
import Messages from '../comm/message/messages';
import FeatureDetection from '../util/feature-detection';
import { merge, fromEvent } from 'rxjs';
import StateModel from '../state/state-model';
import TimeStateModel from '../state/time-state-model';

class BasePlayer extends AbstractPlayer {

    constructor(config, observationCenter) {

        super(config, observationCenter);

        this.log('BasePlayer constructor', config);

        this.onPlayerResize = this.onPlayerResize.bind(this);

        this.stateModel = new StateModel();

        this.timeStateModel = new TimeStateModel();

        this.resizeObserver = new ResizeObserver(this.onPlayerResize);

        this.subscriptions = [];

        /**
         *
         * @type {MediaItem}
         * @desc the currrently playing media item
         *
         */
        this.currentMediaItem = undefined;

    }

    /**
     *
     * @desc Detects supported features in the current browser
     *
     */
    detectFeatures() {

        this.log('Detect features');
        return FeatureDetection.testHTML5Autoplay()
            .then((autoplay) => {

                this.log('features', autoplay);
                this.features = {
                    autoplay,
                };

            });

    }

    /**
     *
     * @desc Initializes the player and creates the DOM Element(s). Calls
     * 1. findParentElement()
     * 2. createPlayerElement()
     * 3. subscribeEvents() the events from the Player to the channel
     * 3. attachPlayerElement()
     *
     */
    initPlayer() {

        return this.detectFeatures().then(() => {

            this.setAutoplayConfig();

            this.findParentElement();

            this.createPlayerElement();

            this.attachPlayerElement();

            this.subscribe();

            this.initListeners();

            const mediaItem = _get(this.playerTypeConfig, 'mediaItem');

            if (mediaItem && mediaItem.src) {

                this.open(mediaItem);

            }

        });

    }

    /**
     *
     * @desc Sets/updates the autoPlay config
     *
     */
    setAutoplayConfig() {

        const {
            autoPlay: autoplayConfig,
            clickToPlay, /* The autoplay happens because of a user generated action
                            (e.g. the user clicked a poster) */
        } = this.playerTypeConfig;
        const regularFeatures = _get(this.features, 'autoplay.regular', false);
        const mutedFeatures = _get(this.features, 'autoplay.muted', false);
        const forceMutedPlayer = !regularFeatures && mutedFeatures && !clickToPlay;

        /** Then change the muted property based on this */
        if (forceMutedPlayer) {

            this.playerTypeConfig.mute = true;

        }

        this.stateModel.setVolumeChangeState(1, this.playerTypeConfig.mute);

    }

    /**
     *
     * @desc Sets the `parentEl` property with a reference to the player node's parent `DOMElement`.
     * The reference to the parent node is based on the `someTarget` passed in with the `MediaPlayerConfig`
     *
     */
    findParentElement() {

        this.parentEl = document.getElementById(this.playerTypeConfig.domTarget);

    }

    /**
     *
     * @desc Request fullscreen for the specified DOM element
     * @param {*} element The DOM element to target for fullscreen
     *
     */
    requestFullscreen(element) {

        this.log('Request fullscreen');
        Fullscreen.requestFullscreen(element);

    }

    /**
     *
     * @desc Exit fullscreen
     *
     */
    exitFullscreen() {

        this.log('exit fullscreen');
        Fullscreen.exitFullscreen();

    }

    /**
     *
     * @desc Logs the message to the console.
     * @param {String} message The message to log.
     * @param {*} object An object to send to the console
     *
     */
    log(...args) {

        const message = args.shift();
        const name = this.toString();
        Log.log(`${name}: ${message}`, args);

    }

    /**
     *
     * @desc Setup event listeners
     *
     */
    initListeners() {

        if (Fullscreen.fullscreenEnabled) {

            Fullscreen.addEventListener('fullscreenchange', this.onFullscreenChange.bind(this));
            Fullscreen.addEventListener('fullscreenerror', this.onFullscreenChange.bind(this));

        }

        this.resizeObserver.observe(this.parentEl.parentNode);

    }

    /**
     *
     * @desc Fullscreen change handler. Set the player state when the player enters or exits fullscreen
     * @param {Event} event
     *
     */
    onFullscreenChange(event) {

        const hasFullscreenElement = !!Fullscreen.fullscreenElement;
        this.stateModel.setFullscreenChangeState(hasFullscreenElement, Fullscreen.fullscreenEnabled);
        this.observationCenter.stateSubject.next(new Messages.StateEventMessage(Messages.StateEventMessage.PLAYER_STATE_UPDATE, null, this.stateModel));

    }

    onFullscreenError(event) {

        this.log('There was a Fullscreen error ', event);

    }

    onPlayerResize(entries) {

        const newSize = entries[0].contentRect;
        this.observationCenter.playerSubject.next(new Messages.BaseMessage(MediaPlayerEvents.MEDIA_PLAYER.PLAYER_RESIZED, null, newSize));

    }

    updateStateSubject(message) {

        if (message.type !== 'empty') {

            const stateSubject = this.observationCenter.stateSubject;
            stateSubject.next(new Messages.StateEventMessage(Messages.StateEventMessage.PLAYER_STATE_UPDATE, message, this.stateModel));

        }

    }

    updatePlayerTimeSubject(message) {

        if(message.type !== 'empty') {

            const playerTimeSubject = this.observationCenter.playerTimeSubject;
            playerTimeSubject.next(new Messages.StateEventMessage(Messages.StateEventMessage.PLAYER_TIME_STATE_UPDATE, message, this.timeStateModel));

        }

    }

    /**
     *
     * @desc Subscribe to player events
     *
     */
    subscribe() {

        const PlayerEvents = this.getPlayerEvents(this.playerTypeConfig.playerType);

        const playerSubject = this.observationCenter.playerSubject;

        const subscription = playerSubject.subscribe((message) => {

            const type = message.type;

            switch(type) {

                case PlayerEvents.MEDIA_LOAD_START: {

                    this.stateModel.setLoadingState(true);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.CANPLAY: {

                    this.stateModel.setLoadingState(false);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.MEDIA_START: {

                    const volume = _get(message, 'event.target.volume');
                    const muted = _get(message, 'event.target.muted');

                    this.stateModel.setMediaStartState(volume, muted);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.MEDIA_PAUSE: {

                    this.stateModel.setMediaPauseState();
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.MEDIA_COMPLETE: {

                    this.stateModel.setMediaCompleteState();
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.MEDIA_SEEKED: {

                    this.timeStateModel.setMediaSeekedState();
                    this.updatePlayerTimeSubject(message);
                    break;

                }

                case PlayerEvents.MEDIA_SEEKING: {

                    this.timeStateModel.setMediaSeekingState();
                    this.updatePlayerTimeSubject(message);
                    break;

                }

                case PlayerEvents.TIME_UPDATE: {

                    this.timeStateModel.setMediaTimeupdateState(_get(message, 'event.target.currentTime'));
                    this.updatePlayerTimeSubject(message);
                    break;

                }

                case PlayerEvents.DURATION_CHANGE: {

                    this.timeStateModel.setMediaDurationChangeState(_get(message, 'event.target.duration'));
                    this.updatePlayerTimeSubject(message);
                    break;

                }

                case PlayerEvents.VOLUME_CHANGE: {

                    const volume = _get(message, 'event.target.volume');
                    const muted = _get(message, 'event.target.muted');

                    this.stateModel.setVolumeChangeState(volume, muted);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.LEVEL_LOADED: {

                    const live = _get(message, 'data.details.live');
                    const targetDuration = _get(message, 'data.details.targetduration');
                    const streamStart = _get(message, 'data.details.fragments[0].start');

                    this.timeStateModel.setLevelLoadedState(live, targetDuration, streamStart);
                    this.updatePlayerTimeSubject(message);
                    break;

                }

                case PlayerEvents.TEXT_TRACKS_AVAILABLE: {

                    this.stateModel.setClosedCaptions(message.data);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.TEXT_TRACK_ENABLED: {

                    this.stateModel.setClosedCaptionsActiveState(true);
                    this.updateStateSubject(message);
                    break;

                }

                case PlayerEvents.TEXT_TRACK_DISABLED: {

                    this.stateModel.setClosedCaptionsActiveState(false);
                    this.updateStateSubject(message);
                    break;

                }

            }

        });

        this.subscriptions.push(subscription);

    }

    /**
     *
     * @desc Unsubscribe from any subjects
     *
     */
    unsubscribe() {

        this.subscriptions.forEach((subscription) => {

            subscription.unsubscribe();

        });

    }

    /**
     *
     * @desc Get the player events based on the config.
     * @param {String} type The player type to get events for
     *
     */
    getPlayerEvents(type) {

        return MediaPlayerEvents[type];

    }

    /**
     *
     * @desc Play media when paused or stopped
     *
     */
    play() {

        this.videoEl.play();

    }

    /**
     *
     * @desc Pause media playback
     *
     */
    pause() {

        this.videoEl.pause();

    }

    /**
     *
     * @desc Sets the currentTime to 0.
     *
     */
    replay() {

        this.videoEl.currentTime = 0;
        this.videoEl.play();

    }

    /**
     *
     * @description Set poster image for the HTML5 player
     * @param {string} imgUrl
     * @memberof HLSJSPlayer
     *
     */
    setPoster(imgUrl) {

        this.videoEl.setAttribute('poster', imgUrl);

    }

    /**
     *
     * @desc Seek to the specified time
     * @param {Number} time The time to seek to
     *
     */
    seek(time) {

        this.videoEl.currentTime = time;

    }

    /**
     *
     * @desc Mute the player
     *
     */
    mute() {

        this.videoEl.muted = true;

    }

    /**
     *
     * @desc Mute the player
     *
     */
    unMute() {

        this.videoEl.muted = false;

    }

    /**
     *
     * @desc Sets the player volume
     * @param {number} volume The volume to set the player to. A value between 0 and 1.
     *
     */
    setVolume(volume) {

        this.videoEl.volume = volume;

    }

    /**
     *
     * @desc Sets isAdPlaying on the state and updates any observers
     * @param {boolean} value if an ad is playing or not
     *
     */
    setIsAdPlaying(value) {

        this.stateModel.setIsAdPlayingState(value);
        this.observationCenter.stateSubject.next(new Messages.StateEventMessage(Messages.StateEventMessage.PLAYER_STATE_UPDATE, null, this.stateModel));

    }

    /**
     *
     * @desc Destroys the BasePlayer
     *
     */
    destroy() {

        this.unsubscribe();
        Fullscreen.removeEventListener('fullscreenchange', this.onFullscreenChange);
        Fullscreen.removeEventListener('fullscreenerror', this.onFullscreenError);

        if (this.parentEl) {

            this.resizeObserver.unobserve(this.parentEl);

        }

    }

    /**
     *
     * @desc Returns the object type as a string
     * @abstract
     *
     */
    toString() {

        return 'BasePlayer';

    }

}

export default BasePlayer;
